import { useState, useRef, useEffect } from "react";
import { Play, Loader2, CheckCircle2, Terminal, GitPullRequest } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SecurityGauge } from "@/components/SecurityGauge";
import { SEVERITY } from "@/lib/security";
import api from "@/lib/api";

const PHASES = ["Cloning target", "SAST engine", "DAST probe", "Dependency audit", "Secret scan", "Cloud posture", "AI correlation"];

export function InteractiveScannerDemo() {
  const [state, setState] = useState("idle"); // idle | scanning | done
  const [logs, setLogs] = useState([]);
  const [findings, setFindings] = useState([]);
  const [score, setScore] = useState(66);
  const [counts, setCounts] = useState({ critical: 3, high: 12, medium: 31, low: 18 });
  const [remediated, setRemediated] = useState(false);
  const logRef = useRef(null);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [logs]);

  const runScan = async () => {
    setState("scanning");
    setLogs([]);
    setFindings([]);
    setRemediated(false);
    let data;
    try {
      const res = await api.get("/demo/scan");
      data = res.data;
    } catch {
      setState("idle");
      return;
    }

    for (let i = 0; i < PHASES.length; i++) {
      await new Promise((r) => setTimeout(r, 420));
      setLogs((l) => [...l, { t: `[${new Date().toLocaleTimeString()}]`, msg: `${PHASES[i]}…`, ok: true }]);
    }
    await new Promise((r) => setTimeout(r, 400));

    const sorted = [...data.findings].sort((a, b) => b.cvss - a.cvss);
    for (let i = 0; i < Math.min(6, sorted.length); i++) {
      await new Promise((r) => setTimeout(r, 260));
      setFindings((f) => [...f, sorted[i]]);
    }
    setCounts(data.severity_counts);
    setScore(data.score_before);
    setState("done");
  };

  const remediate = async () => {
    setRemediated(true);
    const target = 94;
    let cur = score;
    const step = () => {
      cur += 2;
      setScore(Math.min(target, cur));
      if (cur < target) setTimeout(step, 45);
    };
    step();
    setLogs((l) => [
      ...l,
      { t: "[fix]", msg: "AI Security Engineer generated 6 remediation PRs", ok: true },
      { t: "[validate]", msg: "Re-scan complete — findings resolved", ok: true },
    ]);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      {/* Terminal + score */}
      <div className="lg:col-span-3 card-surface rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-[var(--surface-2)]">
          <div className="flex items-center gap-2 text-xs font-mono text-muted">
            <Terminal size={14} className="text-cyber" />
            demo-vulnerable-node-api · main
          </div>
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-crit/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-high/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-secure/60" />
          </div>
        </div>

        <div className="grid sm:grid-cols-5">
          <div ref={logRef} className="sm:col-span-3 h-64 overflow-y-auto p-4 font-mono text-xs space-y-1.5 bg-[#0B0E14]">
            {logs.length === 0 && state === "idle" && (
              <p className="text-muted">$ Press "Run Demo Scan" to watch the AI engineer discover, prioritize, fix and verify.</p>
            )}
            {logs.map((l, i) => (
              <div key={i} className="flex gap-2 fade-up">
                <span className="text-cyber/60">{l.t}</span>
                <span className="text-slate-300 flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-secure" /> {l.msg}
                </span>
              </div>
            ))}
            {state === "scanning" && (
              <div className="flex items-center gap-2 text-cyber">
                <Loader2 size={12} className="animate-spin" /> scanning…
              </div>
            )}
          </div>

          <div className="sm:col-span-2 flex flex-col items-center justify-center p-4 border-t sm:border-t-0 sm:border-l border-line">
            <SecurityGauge score={score} size={150} />
            <div className="mt-3 grid grid-cols-2 gap-2 w-full text-center">
              {Object.entries(counts).map(([k, v]) => (
                <div key={k} className="rounded-lg py-1.5" style={{ background: SEVERITY[k].bg }}>
                  <div className="font-display font-bold text-sm" style={{ color: SEVERITY[k].color }}>{v}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted">{k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-line flex gap-2 bg-[var(--surface-2)]">
          <Button
            data-testid="run-demo-scan-btn"
            onClick={runScan}
            disabled={state === "scanning"}
            className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold"
          >
            {state === "scanning" ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
            {state === "done" ? "Re-run Scan" : "Run Demo Scan"}
          </Button>
          {state === "done" && !remediated && (
            <Button
              data-testid="demo-remediate-btn"
              onClick={remediate}
              variant="outline"
              className="border-secure/50 text-secure hover:bg-secure/10"
            >
              <GitPullRequest size={16} className="mr-2" /> AI Remediate & Validate
            </Button>
          )}
        </div>
      </div>

      {/* Findings list */}
      <div className="lg:col-span-2 card-surface rounded-2xl p-4">
        <h4 className="font-display font-semibold text-ink mb-3 text-sm flex items-center justify-between">
          Prioritized Findings
          {remediated && <Badge className="bg-secure/15 text-secure border-0">Resolved</Badge>}
        </h4>
        <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
          {findings.length === 0 && (
            <p className="text-xs text-muted py-8 text-center">Findings appear here after the scan runs.</p>
          )}
          {findings.map((f, i) => {
            const meta = SEVERITY[f.severity];
            const Icon = meta.icon;
            return (
              <div
                key={i}
                className={`rounded-xl border border-line p-3 fade-up ${remediated ? "opacity-60" : ""}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                    <Icon size={15} style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                      <span className="text-[10px] font-mono text-muted">CVSS {f.cvss}</span>
                    </div>
                    <p className="text-sm text-ink font-medium leading-tight mt-0.5">{f.title}</p>
                    <p className="text-[11px] font-mono text-muted truncate mt-0.5">{f.location}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
